import { z } from 'zod';
import { isValidBusinessId, normalizeBusinessId } from './business-id.js';
// Per-staff tax classification + VAT handling + ניכוי מס במקור. This is an
// identity attribute of the staff member (like their bank details), NOT an
// override-able salary component — so it lives on staff_members, not in
// StaffSalarySettings.
//
//   employee        (שכיר)        — no VAT.
//   exempt_dealer   (עוסק פטור)   — no VAT.
//   licensed_dealer (עוסק מורשה)  — VAT applies; vat_mode decides how:
//       excluded (לפני מע״מ) — the computed amount is pre-VAT; add VAT on top.
//       included (כולל מע״מ) — VAT is already inside the amount; we surface the
//                               embedded VAT portion but don't add to the total.
//
// On top of that, a DEALER (either kind) may carry a withholding rate —
// ניכוי מס במקור: the box withholds that % of the payable total and remits it to
// the tax authority, so the coach receives the remainder. A שכיר never does (they
// are taxed through payroll), which is why the rate is forced to 0 for them.
export const StaffTaxTypeSchema = z.enum(['employee', 'exempt_dealer', 'licensed_dealer']);
export const VatModeSchema = z.enum(['excluded', 'included']);
/** Israeli VAT rate (18% since Jan 2025). Single source of truth — bump here if
 *  the national rate changes. */
export const VAT_RATE = 0.18;
export const TAX_TYPE_LABELS = {
    employee: 'שכיר',
    exempt_dealer: 'עוסק פטור',
    licensed_dealer: 'עוסק מורשה',
};
export const VAT_MODE_LABELS = {
    excluded: 'לפני מע״מ (יתווסף מע״מ)',
    included: 'כולל מע״מ',
};
// ── ניכוי מס במקור (withholding tax at source) ──────────────────────────────
/** Only dealers can carry a withholding rate — a שכיר is taxed through payroll. */
export function withholdingApplies(taxType) {
    return taxType === 'exempt_dealer' || taxType === 'licensed_dealer';
}
/** A staff member's effective withholding rate in percent, [0..100]. Anything
 *  out of range, non-finite, or set on a שכיר resolves to 0 — so a stale rate
 *  left behind by switching tax type can never silently withhold. */
export function effectiveWithholdingPct(profile) {
    if (!withholdingApplies(profile.tax_type))
        return 0;
    const pct = Number(profile.withholding_pct ?? 0);
    if (!Number.isFinite(pct) || pct <= 0)
        return 0;
    return Math.min(100, pct);
}
export const EMPTY_BANK_DETAILS = {
    bank_name: null,
    bank_branch: null,
    bank_account: null,
    bank_account_holder: null,
};
// ── Per-staff profile (tracked + tax + bank), edited in settings/onboarding ──
const NullableTrimmed = z
    .string()
    .trim()
    .max(120)
    .nullable()
    .transform((v) => (v ? v : null));
// Bank branch + account numbers are digits only — strip any non-digit so letters
// can never be saved (the UI also blocks them at input time).
const NullableDigits = z
    .union([z.string(), z.null()])
    .transform((v) => (v ?? '').replace(/\D/g, ''))
    .pipe(z.string().max(40))
    .transform((v) => (v ? v : null));
// The coach's own עוסק number (ת״ז for a sole trader, ח״פ for a company) — what
// their invoices must be issued under. Stored normalized (9 digits); empty ⇒ null,
// and an invoice's issuer is then simply not checked.
export const NullableBusinessId = z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => ((v ?? '').trim() ? normalizeBusinessId(v) ?? '' : null))
    .refine((v) => v === null || isValidBusinessId(v), { message: 'מספר העוסק אינו תקין' });
// ניכוי מס במקור, in percent. Accepts a number or a numeric string (the UI's
// input), clamps to [0..100] and rounds to 2dp — certificates are issued at
// rates like 5 / 10 / 30, but partial-point rates do exist.
const WithholdingPct = z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .transform((v) => {
    const n = typeof v === 'string' ? Number(v.trim()) : Number(v ?? 0);
    if (!Number.isFinite(n) || n <= 0)
        return 0;
    return Math.round(Math.min(100, n) * 100) / 100;
});
export const StaffProfileSchema = z
    .object({
    tracked: z.boolean(),
    tax_type: StaffTaxTypeSchema,
    vat_mode: VatModeSchema,
    withholding_pct: WithholdingPct,
    business_id: NullableBusinessId,
    bank_name: NullableTrimmed,
    bank_branch: NullableDigits,
    bank_account: NullableDigits,
    bank_account_holder: NullableTrimmed,
})
    // A שכיר can't carry a withholding rate. Zero it here rather than rejecting,
    // so switching a coach from עוסק back to שכיר just drops the rate instead of
    // failing the save with a validation error.
    .transform((p) => ({
    ...p,
    withholding_pct: withholdingApplies(p.tax_type) ? p.withholding_pct : 0,
    // Nor does a שכיר issue invoices — a leftover עוסק number would silently
    // start judging an invoice they were never meant to send.
    business_id: withholdingApplies(p.tax_type) ? p.business_id : null,
}));
export const DEFAULT_STAFF_PROFILE = {
    tracked: true,
    tax_type: 'employee',
    vat_mode: 'excluded',
    withholding_pct: 0,
    business_id: null,
    ...EMPTY_BANK_DETAILS,
};
export const DEFAULT_TAX_PROFILE = {
    tax_type: 'employee',
    vat_mode: 'excluded',
    withholding_pct: 0,
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** Given a pre-/gross- amount and a tax profile, resolve the full money
 *  breakdown: subtotal / vat / total, then ניכוי מס במקור off the total.
 *  `amount` is the salary-components sum (what the engine produces today).
 *
 *  The withholding is taken on the INVOICE AMOUNT INCLUDING VAT — e.g. ₪1,000
 *  + 18% VAT = ₪1,180, at 10% → ₪118 withheld → ₪1,062 transferred. */
export function applyTax(amount, profile) {
    const { subtotal, vat, total } = applyVatOnly(amount, profile);
    const withholding_pct = effectiveWithholdingPct(profile);
    // Nothing is withheld from a negative month (an offset larger than the pay):
    // there is no payment to withhold from.
    const withholding = withholding_pct > 0 && total > 0 ? round2(total * (withholding_pct / 100)) : 0;
    return {
        subtotal,
        vat,
        total,
        withholding_pct,
        withholding,
        net_total: round2(total - withholding),
    };
}
/** The VAT half of `applyTax`, without the withholding step. */
function applyVatOnly(amount, profile) {
    if (profile.tax_type !== 'licensed_dealer') {
        return { subtotal: round2(amount), vat: 0, total: round2(amount) };
    }
    if (profile.vat_mode === 'included') {
        // `amount` already includes VAT → back out the embedded portion.
        const subtotal = round2(amount / (1 + VAT_RATE));
        return { subtotal, vat: round2(amount - subtotal), total: round2(amount) };
    }
    // excluded → add VAT on top.
    const vat = round2(amount * VAT_RATE);
    return { subtotal: round2(amount), vat, total: round2(amount + vat) };
}
/** Format a withholding rate for display: "10%", "2.5%". */
export function formatWithholdingPct(pct) {
    return `${Number(pct.toFixed(2))}%`;
}
//# sourceMappingURL=tax.js.map